import { createContext, useContext, useState, useEffect } from 'react';

const LocationContext = createContext(null);

// Default to Melbourne, VIC, Australia
const DEFAULT_LOCATION = {
  lat: -37.8136,
  lng: 144.9631,
  name: 'Melbourne, VIC',
};

export const LocationProvider = ({ children }) => {
  const [location, setLocation] = useState(() => {
    const saved = localStorage.getItem('userLocation');
    return saved ? JSON.parse(saved) : DEFAULT_LOCATION;
  });
  const [locationError, setLocationError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          name: 'Your Location',
        };

        // Try to get location name via reverse geocoding
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${newLocation.lat}&lon=${newLocation.lng}`
          );
          const data = await response.json();
          if (data.address) {
            const city = data.address.city || data.address.town || data.address.village || '';
            const state = data.address.state || '';
            newLocation.name = city && state ? `${city}, ${state}` : data.display_name.split(',')[0];
          }
        } catch (error) {
          console.error('Failed to get location name:', error);
        }

        setLocation(newLocation);
        localStorage.setItem('userLocation', JSON.stringify(newLocation));
        setLocationError(null);
        setIsLoading(false);
      },
      (error) => {
        setLocationError(error.message);
        setIsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 600000 }
    );
  };

  const setManualLocation = (latOrObj, lng, name) => {
    // Support both (lat, lng, name) and ({lat, lng, name}) formats
    let newLocation;
    if (typeof latOrObj === 'object') {
      newLocation = { lat: latOrObj.lat, lng: latOrObj.lng, name: latOrObj.name };
    } else {
      newLocation = { lat: latOrObj, lng, name };
    }
    setLocation(newLocation);
    localStorage.setItem('userLocation', JSON.stringify(newLocation));
    setLocationError(null);
  };

  // Don't auto-request location on first load - use Melbourne default
  // User can manually update if they want

  const value = {
    location,
    locationError,
    isLoading,
    requestLocation,
    setManualLocation,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};
